import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { School, Target, Lightbulb } from "lucide-react";
import ImageCarousel from "../components/ImageCarousel";

export const About: React.FC = () => {
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: (i: number = 1) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.2, duration: 0.6 },
    }),
  };

  return (
    <main className="text-gray-800 overflow-x-hidden w-full">
      {/* Top Banner */}
      <section className="relative w-full h-64 md:h-80 overflow-hidden">
        <ImageCarousel autoPlay={true} interval={4000} />
        <div className="absolute inset-0 bg-blue-900/70 flex items-center justify-center">
          <motion.h1
            initial={{ x: "-100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-white text-3xl md:text-5xl font-extrabold tracking-wide drop-shadow-lg text-center px-4"
          >
            About
          </motion.h1>
        </div>
      </section>

      {/* Who We Are */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="max-w-6xl mx-auto px-4 md:px-8 py-16 overflow-x-hidden"
      >
        <motion.div
          custom={1}
          variants={fadeInUp}
          className="flex items-center gap-3 mb-8 bg-yellow-100 border-l-4 border-yellow-400 px-4 py-2 rounded shadow-sm"
        >
          <School className="text-blue-900 w-6 h-6" />
          <h2 className="text-2xl md:text-3xl font-extrabold text-blue-900">
            Who We Are
          </h2>
        </motion.div>

        <motion.p
          custom={2}
          variants={fadeInUp}
          className="text-gray-700 max-w-3xl mx-auto text-base leading-relaxed"
        >
          Sainik and Military Schools are prestigious institutions that combine
          academic excellence with military discipline. Gaining admission to
          these schools is highly competitive, requiring focused preparation and
          expert guidance. Haifa Defence Academy is dedicated to bridging this
          gap by providing a comprehensive, well-structured program designed to
          equip students with the knowledge, skills, and confidence needed to
          excel in the entrance exams—and thrive beyond them.
        </motion.p>
      </motion.section>

      {/* Our Objectives */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="bg-blue-50 max-w-6xl mx-auto px-4 md:px-8 py-16 rounded-xl overflow-x-hidden"
      >
        <motion.div
          custom={1}
          variants={fadeInUp}
          className="flex items-center gap-3 mb-8 bg-yellow-100 border-l-4 border-yellow-400 px-4 py-2 rounded shadow-sm"
        >
          <Target className="text-blue-900 w-6 h-6" />
          <h2 className="text-2xl md:text-3xl font-extrabold text-blue-900">
            Our Objectives
          </h2>
        </motion.div>

        <motion.ul
          custom={2}
          variants={fadeInUp}
          className="text-gray-700 max-w-3xl mx-auto list-disc list-inside space-y-4"
        >
          <li>
            Provide expert coaching for Sainik School & Military School entrance
            exams.
          </li>
          <li>
            Inculcate leadership, discipline, and strong academic foundations.
          </li>
          <li>
            Create a competitive and motivating environment for 100 students
            (classes 5–10).
          </li>
          <li>
            Focus on core subjects including Maths, Science, English, and
            Reasoning.
          </li>
          <li>
            Holistic development through personality and physical training.
          </li>
        </motion.ul>
      </motion.section>

      {/* Mission and Vision */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="max-w-6xl mx-auto px-4 md:px-8 py-16 overflow-x-hidden"
      >
        <div className="grid md:grid-cols-2 gap-12">
          {/* Mission */}
          <motion.div
            custom={1}
            variants={fadeInUp}
            className="text-center space-y-5"
          >
            <div className="flex justify-center items-center gap-2 bg-yellow-100 border-l-4 border-yellow-400 px-4 py-2 rounded shadow-sm">
              <Lightbulb className="text-yellow-500 w-6 h-6" />
              <h2 className="text-2xl font-bold text-blue-900">Our Mission</h2>
            </div>
            <p className="text-base text-gray-700 leading-relaxed">
              At Haifa Defence Academy, we understand that every student aspires
              to build a meaningful career. While schools lay the academic
              foundation, our academy provides the structured guidance,
              discipline, and mindset required to go further. Whether their path
              leads to the armed forces, entrepreneurship, or academic
              excellence, we empower students with the confidence and capability
              to succeed in all endeavors.
            </p>
          </motion.div>

          {/* Vision */}
          <motion.div
            custom={2}
            variants={fadeInUp}
            className="text-center space-y-5"
          >
            <div className="flex justify-center items-center gap-2 bg-yellow-100 border-l-4 border-yellow-400 px-4 py-2 rounded shadow-sm">
              <Lightbulb className="text-yellow-500 w-6 h-6" />
              <h2 className="text-2xl font-bold text-blue-900">Our Vision</h2>
            </div>
            <p className="text-base text-gray-700 leading-relaxed">
              The vision of Haifa Defence Academy is to transform determined
              students into exceptional cadets through rigorous physical,
              academic, and mental training. Our mission is to guide them
              towards distinguished careers in the NDA, CDS, and other elite
              branches of the Defence Forces.
            </p>
          </motion.div>
        </div>
      </motion.section>

      {/* Contact CTA */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        animate={{ opacity: 1 }}
        variants={fadeInUp}
        className="bg-gradient-to-r from-yellow-400 to-orange-300 text-blue-900 py-12 overflow-x-hidden"
      >
        <div className="max-w-4xl mx-auto text-center px-4">
          <h3 className="text-2xl font-bold mb-3">Contact Us</h3>
          <p className="text-md mb-6">
            Have questions or need help? Get in touch and start your journey
            with us today.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 justify-center bg-blue-900 text-white px-6 py-3 rounded shadow hover:bg-blue-800 transition text-lg font-semibold"
          >
            Contact Us
          </Link>
        </div>
      </motion.section>

      {/* WhatsApp CTA */}
      <a
        href="https://wa.me/919997450792?text=Hi%20I%20am%20interested%20in%20your%20academy%20program"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white px-5 py-3 rounded-full transition-all duration-300 animate-pulse font-semibold shadow-lg"
        title="Chat on WhatsApp"
        style={{
          boxShadow: `
            0 0 15px rgba(0, 255, 0, 0.6),
            0 0 25px rgba(0, 255, 0, 0.5),
            0 4px 10px rgba(0, 0, 0, 0.3)
          `,
        }}
      >
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
          alt="WhatsApp"
          className="h-8 w-8"
        />
        <span className="hidden sm:inline">Chat</span>
      </a>
    </main>
  );
};
